import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import './CollapsiblePanel.sass';

import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
// @ts-expect-error
import scrollToWithAnimation from 'scrollto-with-animation';

export enum CollapsiblePanelStyleVariant {
  STANDARD = 'standard',
  OUTSIZE = 'outsize',
  MINIMAL = 'minimal',
  UNSTYLED = 'unstyled',
  BAR = 'bar',
}

type Props = {
  openControlIcon?: TIconName;
  closeControlIcon?: TIconName;
  delayMs?: number;
  title?: string | JSX.Element;
  children?: JSX.Element | JSX.Element[];
  iconOnlyClick?: boolean;
  isDisabled?: boolean;
  isOpen: boolean;
  showControlIcons?: boolean;
  transitionDurationMs?: number;
  shouldRenderHeader?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  styleVariant?: CollapsiblePanelStyleVariant;
  watchSize?: any; // Property that will trigger resize on change
  scrollParentReference?: React.RefObject<HTMLDivElement | null>;
};

const CollapsiblePanel: FunctionComponent<Props> = ({
  openControlIcon,
  closeControlIcon,
  delayMs = 0,
  title,
  children,
  iconOnlyClick = false,
  isDisabled = false,
  isOpen,
  showControlIcons = true,
  transitionDurationMs = 300,
  shouldRenderHeader = true,
  onOpen,
  onClose,
  styleVariant = CollapsiblePanelStyleVariant.STANDARD,
  watchSize,
  scrollParentReference,
}: Props): JSX.Element => {
  const bodyReference = useRef<HTMLDivElement>(null);
  const [bodyContainerHeight, setBodyContainerheight] = useState(0);

  const getBodyContainerHeight = useCallback(() => {
    if (!isOpen) {
      return 0;
    }
    return bodyReference?.current?.clientHeight || 0;
  }, [isOpen, bodyReference]);

  const transitionDuration = useMemo(
    () => `${transitionDurationMs}ms`,
    [transitionDurationMs]
  );

  const scrollToPanelContent = useCallback(() => {
    if (!scrollParentReference?.current) return;

    const scrollParentOffsetTop = scrollParentReference.current.offsetTop;

    // todo rename

    const parentElementClientHeight =
      scrollParentReference.current.clientHeight;

    const materialsPanelOffsetTop = bodyReference?.current?.offsetTop || 0;

    const materialsPanelOffsetHeight =
      bodyReference?.current?.offsetHeight || 0;

    const materialsPanelTopScroll =
      materialsPanelOffsetTop - scrollParentOffsetTop;

    const materialsPanelBottomScroll =
      materialsPanelOffsetHeight +
      materialsPanelOffsetTop -
      parentElementClientHeight;

    scrollToWithAnimation(
      scrollParentReference.current,
      'scrollTop',
      Math.min(materialsPanelBottomScroll, materialsPanelTopScroll),
      300,
      'easeInOutCirc'
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'scrollParentReference.current.offsetTop', 'scrollParentReference.current.clientHeight', and 'scrollParentReference.current'
  }, []);

  const onHeaderClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (isOpen) {
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onClose && onClose();
    } else {
      // oxlint-disable-next-line eslint(no-unused-expressions) -- FIXME: pre-existing error: Expected expression to be used
      onOpen && onOpen();
      scrollToPanelContent();
    }
  }, [isDisabled, isOpen, onClose, onOpen, scrollToPanelContent]);

  useEffect(() => {
    setBodyContainerheight(getBodyContainerHeight());
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'getBodyContainerHeight'
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setBodyContainerheight(getBodyContainerHeight());
    }, delayMs);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'getBodyContainerHeight'
  }, [isOpen, bodyReference, children, delayMs, watchSize]);

  const openIcon = useMemo(() => {
    let icon: TIconName = 'chevron-right';
    if (openControlIcon) {
      icon = openControlIcon;
    } else if (styleVariant === CollapsiblePanelStyleVariant.BAR) {
      icon = 'chevron-down';
    }
    return <UntitledIcon name={icon} className="open-control-icon" />;
  }, [openControlIcon, styleVariant]);

  const closeIcon = useMemo(() => {
    let icon: TIconName = 'chevron-down';
    if (closeControlIcon) {
      icon = closeControlIcon;
    } else if (styleVariant === CollapsiblePanelStyleVariant.BAR) {
      return null;
    }
    return <UntitledIcon name={icon} className="close-control-icon" />;
  }, [closeControlIcon, styleVariant]);

  const onTitleClick = (e: any) => {
    if (iconOnlyClick) {
      e.stopPropagation();
    }
  };

  const onIconContainerClick = (e: any) => {
    if (styleVariant === CollapsiblePanelStyleVariant.BAR && isOpen) {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={`collapsible-panel${isOpen ? ' open' : ''}${
        isDisabled ? ' disabled' : ''
      } collapsible-panel-style-${styleVariant}`}
      style={{ transitionDuration: transitionDuration }}
    >
      {shouldRenderHeader && (
        <div
          className="collapsible-panel-header"
          onClick={onHeaderClick}
          style={{ transitionDuration: transitionDuration }}
        >
          <div className="title" onClick={onTitleClick}>
            {title}
          </div>
          {showControlIcons && (
            <span
              className="control-icon-container"
              onClick={onIconContainerClick}
            >
              <span>{openIcon}</span>
              <span>{closeIcon}</span>
            </span>
          )}
        </div>
      )}
      <div
        className="collapsible-panel-body-container"
        style={{
          height: bodyContainerHeight,
          transitionDuration: transitionDuration,
        }}
      >
        <div className="collapsible-panel-body" ref={bodyReference}>
          {children}
          {styleVariant === CollapsiblePanelStyleVariant.BAR && (
            <span className="control-icon-container" onClick={onHeaderClick}>
              <span>
                <UntitledIcon
                  name="chevron-up"
                  className="close-control-icon"
                />
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollapsiblePanel;
